import type {ConsolidatedReviewState} from '@github-ui/mergebox/sections/ReviewerSection'
import {MiniMergeStatusItem} from './MiniMergeStatusItem'
import type {MiniStatusIconProps} from './MiniStatusIcon'
import {useGoToMergeBox} from '../../../mergebox/use-go-to-mergebox'
import {useMergeabilityData, type MergeabilityData} from '../use-mergeability-data'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Suspense} from 'react'
import {MiniStatusSkeletonItem} from './MiniMergeStatusLoading'

type ReviewerSection = Pick<
  MergeabilityData['reviewerSection'],
  | 'numReviewsRequired' // the number of approvals required to merge the pull request from rules
  | 'codeownersRequired'
  | 'latestOpinionatedReviews'
  | 'pendingRequestedReviews'
  | 'sectionStatus'
>

export function ApprovalsMiniStatus() {
  const data = useMergeabilityData()
  const goToMergeBox = useGoToMergeBox()

  const {successful, total} = data ? getApprovalsRatio(data.reviewerSection) : {successful: 0, total: 0}
  const iconType = data ? iconTypeByReviewsState[data.reviewerSection.sectionStatus] : 'error'
  const status = data ? reviewStatusText[data.reviewerSection.sectionStatus] : 'Approvals unavailable'

  return (
    <MiniMergeStatusItem aria-label="Approvals">
      <MiniMergeStatusItem.LabelRow>
        <MiniMergeStatusItem.Label icon={iconType} successful={successful} status={status} total={total}>
          Approvals
        </MiniMergeStatusItem.Label>
        <MiniMergeStatusItem.Action
          aria-label="View approvals"
          // TODO: Update the panel to open the approvals section in the merge status panel. The current open panel options
          // don't support expanding sections within the merge status panel
          onClick={event => goToMergeBox(event.currentTarget)}
        />
      </MiniMergeStatusItem.LabelRow>
      <MiniMergeStatusItem.Progress
        aria-label={`${successful} of ${total} approvals`}
        successful={successful}
        total={total}
      />
    </MiniMergeStatusItem>
  )
}

export function ApprovalsMiniStatusBoundary({children}: {children: React.ReactNode}) {
  const goToMergeBox = useGoToMergeBox()

  return (
    <ErrorBoundary boundaryName="ApprovalsMiniStatusBoundary" fallback={null}>
      <Suspense fallback={<MiniStatusSkeletonItem text="Approvals" action={goToMergeBox} />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

function getApprovalsRatio({
  numReviewsRequired,
  codeownersRequired,
  latestOpinionatedReviews,
  pendingRequestedReviews,
}: ReviewerSection) {
  const approvedReviews = latestOpinionatedReviews.filter(review => review.state === 'APPROVED')
  const approvedReviewCount = approvedReviews.filter(review => review.authorCanPushToRepository).length

  if (codeownersRequired) {
    // Manually build codeowner approval ratio because the rule doesn't expose the number of required codeowners - only the fulfillment.
    // The rule doesn't expose how many have been fulfilled or how many are pending, so we have to count the pending codeowners
    // and add them to the approved count to get a total. This can inflate the total count if an approval is received
    // from a non-codeowner, but this is the best we can do with the data we have.
    const pendingCodeownerCount = pendingRequestedReviews.filter(request => request.isCodeOwner).length
    return {
      successful: approvedReviewCount,
      // Take the most restrictive count between the two rules so that we don't undercount the total required approvals.
      total: Math.max(numReviewsRequired, approvedReviewCount + pendingCodeownerCount),
    }
  }

  return {successful: approvedReviewCount, total: numReviewsRequired}
}

const iconTypeByReviewsState: Record<ConsolidatedReviewState, MiniStatusIconProps['type']> = {
  APPROVED: 'success',
  CHANGES_REQUESTED: 'failure',
  REVIEW_REQUIRED: 'warning',
  REVIEWED: 'success',
  REVIEW_REQUESTED: 'warning',
}

const reviewStatusText: Record<ConsolidatedReviewState, string> = {
  APPROVED: 'Approved',
  CHANGES_REQUESTED: 'Changes requested',
  REVIEW_REQUIRED: 'Review required',
  REVIEWED: 'Reviewed',
  REVIEW_REQUESTED: 'Review requested',
}
